import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DotField } from '../DotField';
import { CUST_GEN_DOT_FILL, CUST_GEN_DOT_FILL_LIGHT } from '../../../../theme/customizer-tokens';
import { useLogoFrame } from '../../lib/useLogoFrame';
import { Card, ImageFrame, LogoStack, LogoBlurred, LogoSharp, Caption, GenOverlay, Label } from './LogoSlot.styles';

/** Typewriter cadence (ms). FADE_MS must match the Label's CSS opacity transition. */
const TYPE_MS = 52;
const HOLD_MS = 1900;
const FADE_MS = 320;
const GAP_MS = 120;
/** Reveal cadence (ms). FADE_IN_MS = the logo's opacity 0→1 beneath the grid;
 *  GRID_FADE_MS must match the grid's CSS opacity transition + the deblur. */
const FADE_IN_MS = 1000;
const GRID_FADE_MS = 760;

export interface LogoSlotProps {
  /** The generated logo URL once it has streamed in, or null while still generating. */
  src: string | null;
  /** Per-logo blurb (desktop caption); omitted on the compact mobile card. */
  caption?: string;
  selected: boolean;
  onSelect: () => void;
  /** Compact = mobile list card (shorter, no caption). */
  compact?: boolean;
  /** The slot's position. Seeds the field + staggers the caption so a stack of
   *  slots never animates in lockstep. */
  index: number;
}

/**
 * A single logo slot. It is the SAME card across the whole lifecycle — the
 * {@link DotField} grid stays mounted from "generating" straight through the
 * reveal (no remount, so the animation never restarts). When the logo streams in
 * it fades in (0→1 over 1s) BENEATH the still-animating grid, then the blur lifts
 * as the grid cross-fades out and unmounts.
 */
export const LogoSlot = ({ src, caption, selected, onSelect, compact = false, index }: LogoSlotProps): React.ReactElement => {
  const { t } = useTranslation();

  // Literal t() calls so the strings are harvested + stay reactive to language;
  // held in a ref so the typewriter effect never restarts on the rapid re-renders.
  const messages = [
    t('Generating your logo…'),
    t('Sketching the concept…'),
    t('Exploring colors…'),
    t('Refining the details…'),
    t('Balancing the composition…'),
  ];
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const frame = useLogoFrame(src);

  const [label, setLabel] = useState('');
  const [visible, setVisible] = useState(true);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [shown, setShown] = useState(false);
  const [deblur, setDeblur] = useState(false);
  const [done, setDone] = useState(false);

  // Reset the reveal whenever the source changes (incl. back to null on refresh).
  useEffect(() => {
    setImgLoaded(false);
    setShown(false);
    setDeblur(false);
    setDone(false);
  }, [src]);

  // Reveal sequence, once the (present) logo has decoded.
  useEffect(() => {
    if (!imgLoaded) return undefined;
    setShown(true); // logo fades 0→1 over FADE_IN_MS, beneath the grid
    const t1 = window.setTimeout(() => setDeblur(true), FADE_IN_MS); // deblur + grid fade out
    const t2 = window.setTimeout(() => setDone(true), FADE_IN_MS + GRID_FADE_MS); // drop the grid
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [imgLoaded]);

  // Typewriter caption — runs while the grid is up, stops once it's gone.
  useEffect(() => {
    if (done) return undefined;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (reduce) {
      const list = messagesRef.current;
      setLabel(list[index % list.length] ?? '');
      setVisible(true);
      return undefined;
    }

    let msg = index % messagesRef.current.length;
    let chars = 0;
    let timer = 0;

    function typeChar(): void {
      const full = messagesRef.current[msg] ?? '';
      if (chars < full.length) {
        chars += 1;
        setLabel(full.slice(0, chars));
        timer = window.setTimeout(typeChar, TYPE_MS);
      } else {
        timer = window.setTimeout(fadeOut, HOLD_MS);
      }
    }
    function fadeOut(): void {
      setVisible(false);
      timer = window.setTimeout(next, FADE_MS);
    }
    function next(): void {
      msg = (msg + 1) % messagesRef.current.length;
      chars = 0;
      setLabel('');
      setVisible(true);
      timer = window.setTimeout(typeChar, GAP_MS);
    }

    timer = window.setTimeout(typeChar, 300 + (index % 4) * 220);
    return () => window.clearTimeout(timer);
  }, [index, done]);

  const hasLogo = src !== null;

  return (
    <Card
      type="button"
      $selected={selected}
      $compact={compact}
      $frame={frame}
      onClick={hasLogo ? onSelect : undefined}
      aria-pressed={hasLogo ? selected : undefined}
      aria-label={hasLogo ? t('Select logo') : undefined}
    >
      <ImageFrame $compact={compact}>
        {hasLogo ? (
          <LogoStack>
            <LogoBlurred src={src} alt="" $shown={shown} $deblur={deblur} onLoad={() => setImgLoaded(true)} />
            <LogoSharp src={src} alt="" $deblur={deblur} />
          </LogoStack>
        ) : null}
      </ImageFrame>
      {/* Desktop: the caption slot is ALWAYS reserved (fixed card height, no reflow);
          the description fades in only once the reveal has finished. */}
      {compact ? null : <Caption text={caption ?? ''} $shown={done && hasLogo && !!caption} $frame={frame} tag="paragraph" />}
      {done ? null : (
        <GenOverlay $fading={deblur} aria-hidden="true">
          <DotField seed={index} fill={frame === 'light' ? CUST_GEN_DOT_FILL_LIGHT : CUST_GEN_DOT_FILL} />
          <Label text={label} tag="span" $visible={visible} $frame={frame} />
        </GenOverlay>
      )}
    </Card>
  );
};

export default LogoSlot;
